import { useEffect, useRef, type ReactNode } from 'react';

import { afterAnimations } from './motion';

/**
 * Wraps a list item that may be on its way out. While `leaving`, the item fades and gives
 * up its height (`animate-collapse`); once that has ended, `onLeft(id)` tells the owner to
 * drop it. Until then it is inert: out of the tab order, unfocusable and hidden from
 * assistive technology, so a decision cannot be repeated on a card that is already gone.
 */
export function Leaving<K>({
  id,
  leaving,
  onLeft,
  className,
  children,
}: {
  id: K;
  leaving: boolean;
  onLeft: (id: K) => void;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!leaving) return;
    let cancelled = false;
    void afterAnimations(ref.current).then(() => {
      if (!cancelled) onLeft(id);
    });
    return () => {
      cancelled = true;
    };
  }, [leaving, id, onLeft]);

  return (
    <div
      ref={ref}
      className={[leaving ? 'animate-collapse' : '', className].filter(Boolean).join(' ')}
      inert={leaving || undefined}
    >
      {children}
    </div>
  );
}
